import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router'
import { L10n } from '@syncfusion/ej2-base'

import {
  DialogSystemGLPure,
  DialogYesNoHLPure,
  SimpleLoading,
  SyncfusionFileManager,
} from '../../pures'
import { LocalStorageService, CategoryService, DigitalVaultDocumentService, DigitalVaultService, CacheStorageService, ClientService } from '../../../services'
import {
  convertToStandarDate,
  getNavigatorTimezone,
  parseCustomDate,
  setCreditsToDocuments,
} from '../../../utils'

/**
 * @author Edgar Escobedo
 * @description - Component to manage data fetching and actions for Syncfusion's File Manager implementation.
 * @param {Array<{ id: string, title: string }>} breadcrumbItems - Array to construct the breadcrumbs of the document.
 * @param {Array} companies - Array of companies.
 * @param {string} fileManagerPath - String where the file manager will be initialized.
 * @param {Object} selectedCompany - Object with information about the user's selected company.
 * @param {function} setDocumentId - Function to set the id of the selected document.
 * @param {function} setFileManagerPath - Function to set the file manager path.
 * @param {function} setSelectedCategory - Function to set the current category.
 * @returns {JSX.element}
 */
function SyncfusionFileManagerAction({
  breadcrumbItems,
  companies,
  fileManagerPath,
  selectedCompany,
  setBreadcrumbItems,
  setDocumentId,
  setFileManagerPath,
  setSelectedCategory,
  subcategoryId,
  setSubcategoryId,
}) {
  const { t } = useTranslation()
  const history = useHistory()
  const { state } = useLocation()
  const ENV = process.env

  const [isLoading, setIsLoading] = useState(true)
  const [firstLoad, setFirstLoad] = useState(true)

  const [companyId, setCompanyId] = useState(selectedCompany.company.id)

  const [categoriesData, setCategoriesData] = useState([])
  const [documentsData, setDocumentsData] = useState([])
  const [permission, setPermission] = useState({})

  const url = process.env.REACT_APP_MIFIRMA_API
  const localStorageService = new LocalStorageService()
  const language =
    localStorageService.getLocalStorageItem('preferredLanguage') || 'es'
  const timezone = getNavigatorTimezone()
  const token = localStorageService.getLocalStorageItem('token').idToken
  const categoryService = new CategoryService(url)
  const digitalVaultDocumentService = new DigitalVaultDocumentService(url)
  const digitalVaultService = new DigitalVaultService(url)
  const cacheService = new CacheStorageService()
  const clientPreferencesService = new ClientService(url)

  const [showAddFileBtn, setShowAddFileBtn] = useState(false)
  const [strBtn, setStrBtn] = useState(t('fileManager.toolbar.newCategory'))
  const [fileData, setFileData] = useState([])
  const [pendingSubcategoryId, setPendingSubcategoryId] = useState(null)

  const [showLoadingRequesting, setShowLoadingRequesting] = useState(false)
  const [showCompletedRequesting, setShowCompletedRequesting] = useState(false)
  const [showErrorRequesting, setShowErrorRequesting] = useState(false)
  const [messageDialog, setMessageDialog] = useState('')
  const [successDialogTitle, setSuccessDialogTitle] = useState('Govo')

  const [showFilesCopied, setShowFilesCopied] = useState(false)
  const [filesSuccess, setFilesSuccess] = useState(0)
  const [filesSuccessNames, setFfilesSuccessNames] = useState([])
  const [filesError, setFilesError] = useState(0)
  const [filesErrorNames, setFilesErrorNames] = useState([])
  const [actionType, setActionType] = useState('')
  const [showDeleteWarning, setShowDeleteWarning] = useState(false)
  const [deleteWarningTitle, setDeleteWarningTitle] = useState('')
  const [deleteWarningMessage, setDeleteWarningMessage] = useState('')
  const [showModalEntities, setShowModalEntities] = useState(false)
  const [documentsToOCR, setDocumentsToOCR] = useState([])
  const [totalCredits, setTotalCredits] = useState(0)
  const [documentSelectedId, setDocumentSelectedId] = useState(null)
  const [fileDetails, setFileDetails] = useState({})

  /** @type {React.MutableRefObject<import('@syncfusion/ej2-react-filemanager').FileManagerComponent | null>} */
  const fileObj = useRef(null)
  const pathRef = useRef('')
  const isFile = useRef(false)

  function checkDepth({ str }) {
    if (!str) return 1
    const parts = str.replace(/^[/\\]+|[/\\]+$/g, '').split(/[/\\]+/)
    return parts.filter(Boolean).length + 2
  }

  async function refreshFileManager(refresh = true, id = null) {
    await fetchAndSetCategories({ refresh, setLoading: false })
    const { current } = fileObj

    if (checkDepth({ str: current.originalPath }) >= 4) {
      const idsArr = current.originalPath.split('/')
      const subId = idsArr[idsArr.length - 2]

      if (String(id) !== subId) {
        setPendingSubcategoryId(subId)
      }
    }

    if (current.refresh) current.refresh()
  }

  function toggleLoadingRequesting(value) {
    setShowLoadingRequesting(value)
  }

  function toggleCompletedRequesting(message, refresh, id, customTitle) {
    if (customTitle) setSuccessDialogTitle(customTitle)
    else setSuccessDialogTitle('Govo')
    setShowCompletedRequesting(true)
    setMessageDialog(message)

    setTimeout(() => {
      setShowCompletedRequesting(false)
      // Restaurar título por defecto para futuras operaciones
      setSuccessDialogTitle('Govo')
    }, 4000)

    refreshFileManager(refresh, id)
  }

  function toggleErrorRequesting(message) {
    setShowErrorRequesting(true)
    setMessageDialog(message)

    setTimeout(() => {
      setShowErrorRequesting(false)
    }, 4000)
  }

  async function fetchAndSetCategories({
    refresh = false,
    setLoading = false,
  }) {
    if (setLoading) setIsLoading(true)
    try {
      const response = await categoryService.getAllCategoriesByCompanyId(
        {
          companyId: companyId,
          timezone: timezone,
          token: token,
          origin: ENV.REACT_APP_ENDPOINT_CATEGORIES_BY_COMPANY_ORIGIN_FILE_MANAGER
        },
        refresh
      )

      let categories
      if (response && response.data && Number(response.statusCode) === 200) {
        categories = response.data.fileData
        setPermission(response.data.permission)
        setCategoriesData(categories)
      }

      const data = categories.map((cat) => ({
        dateCreated: convertToStandarDate(cat.dateCreated, language),
        dateModified: convertToStandarDate(cat.dateModified, language),
        expirationDate: '-',
        statusIA: t('fileManager.listView.header.status.ia.na'),
        filterPath: cat.filterPath,
        hasChild: cat.hasChild,
        id: cat.id,
        isFile: cat.isFile,
        name: cat.key ? t(`fileManager.${cat.key}`) : cat.name,
        parentId: cat.parentId,
        size: cat.size === 0 ? '-' : cat.size,
        type: cat.type,
        imageUrl: cat.imageUrl,
        levelUser: cat.levelUser,
        key: cat.key ?? null,
        logical: cat.logical,
      }))

      setFileData(data)
      return { categories, data }
    } catch (error) {
      console.log(error)
    } finally {
      if (setLoading) setIsLoading(false)
    }
  }

  /**
   * Obtiene un mensaje de error a partir del código de estado devolviendo
   * una traducción específica si existe. Las claves de traducción siguen la
   * convención fileManager.copyFile.dialog.error.{statusCode}. Si no hay
   * traducción específica se devuelve el mensaje genérico.
   * @param {number|string} statusCode
   */
  const getCopyMoveErrorMessage = useCallback((statusCode) => {
    if (!statusCode) return t('fileManager.copyFile.dialog.error')
    const key = `fileManager.copyFile.dialog.error.${statusCode}`
    const translated = t(key)
    // Si la clave no existe, t devuelve la misma clave. Comprobamos este caso.
    if (translated && translated !== key) return translated
    return t('fileManager.copyFile.dialog.error')
  }, [t])

  async function getCategories() {
    if (firstLoad) setIsLoading(true)
    await fetchAndSetCategories({ refresh: false, setLoading: true })
    setFirstLoad(false)
  }

  async function getDocumentsBySubcategory(subcategoryPath, refresh, fileDetails = {}) {
    try {
      const limit = 0
      const offset = 0
      const logical = fileDetails.logical || false
      const key = fileDetails.key || null
      const response = await categoryService.getDocumentsBySubcategory(
        subcategoryPath,
        companyId,
        limit,
        offset,
        token,
        refresh,
        logical,
        key
      )

      let documents
      if (response && response.data && Number(response.statusCode) === 200) {
        documents = response.data
        setDocumentsData(documents)
      }

      const data = documents.map(doc => {
        let statusIA = doc.type === '.pdf' ? t('fileManager.listView.header.status.ia.no.analized') : t('fileManager.listView.header.status.ia.na')
        if (doc.entitiesStatus === ENV.REACT_APP_EVENT_TRANSACTION_STATUS_OCR_PENDING || doc.entitiesStatus === ENV.REACT_APP_EVENT_TRANSACTION_STATUS_OCR_PROCESSING) statusIA = t('fileManager.listView.header.status.ia.processing')
        if (doc.entitiesStatus === ENV.REACT_APP_EVENT_TRANSACTION_STATUS_OCR_EXTRACTED) statusIA = t('fileManager.listView.header.status.ia.extracted')
        if (doc.isShared) doc.shared = t('fileManager.listView.content.shared')
        else doc.shared = t('fileManager.listView.content.private')

        const data = {
          dateCreated: convertToStandarDate(doc.dateCreated, language),
          dateModified: convertToStandarDate(doc.dateModified, language),
          shared: doc.isShared,
          expirationDate: (doc.validationDate) ? convertToStandarDate(doc.validationDate, language) : '-',
          filterPath: doc.filterPath,
          hasChild: doc.hasChild,
          id: doc.id,
          isFile: doc.isFile,
          name: doc.name,
          parentId: doc.parentId,
          size: doc.size === 0 ? '-' : doc.size,
          type: doc.type,
          imageUrl: doc.imageUrl,
          statusIA,
          logical: doc.logical || false,
          key: doc.key || '',
        }
        return data
      })

      // Create a map for faster lookups
      const fileDataMap = new Map(fileData.map((item) => [item.id, item]))

      // Update existing items or add new ones
      data.forEach((item) => {
        fileDataMap.set(item.id, item)
      })

      setFileData(Array.from(fileDataMap.values()))
    } catch (error) {
      console.log(error)
    }
  }

  async function createNewCategory({
    args,
    author = null,
    categoryName,
    companyId,
    createAllCompanies,
    defined = 'Custom',
    description = null,
    icon = null,
    key = null,
    level,
    parent = null,
    type,
  }) {
    toggleLoadingRequesting(true)
    try {
      const response = await categoryService.createNewCategory(
        categoryName,
        icon,
        key,
        description,
        defined,
        companyId,
        type,
        level,
        parent,
        createAllCompanies,
        token
      )

      if (response.data && response.statusCode === 200) {
        toggleLoadingRequesting(false)

        if (createAllCompanies) {
          toggleCompletedRequesting(
            t('fileManager.createCategory.dialog.completed.all'),
            true,
            null,
            'Govo'
          )
        } else {
          toggleCompletedRequesting(
            t('fileManager.createCategory.dialog.completed'),
            true,
            null,
            'Govo'
          )
        }
      } else {
        let errorMsg = t('fileManager.createCategory.dialog.error')

        switch (response.statusCode) {
          case 406:
            errorMsg = t('fileManager.createCategory.dialog.errorInputText')
            break
          case 410:
            errorMsg = t('fileManager.createCategory.dialog.error.410')
            break
          case 415:
            errorMsg = t('fileManager.createCategory.dialog.error.415')
            break
          case 420:
            errorMsg = t('fileManager.createCategory.dialog.error.420')
            break
          case 440:
            errorMsg = t('fileManager.createCategory.dialog.error.440')
            break
          case 450:
            errorMsg = t('fileManager.createCategory.dialog.error.450')
            break
          default:
            errorMsg = t('fileManager.createCategory.dialog.error')
            break
        }
        toggleLoadingRequesting(false)
        toggleErrorRequesting(errorMsg)
      }
    } catch (error) {
      toggleLoadingRequesting(false)
      toggleErrorRequesting(t('fileManager.createCategory.dialog.error'))
      console.error('Error creating new category:', error)
    }
  }

  async function copyFiles({ companyId, files, target, timezone }) {
    toggleLoadingRequesting(true)
    try {
      const response = await categoryService.copyFiles({
        companyId,
        files,
        target,
        timezone,
        token,
      })

      if (response.data && response.statusCode === 200) {
        const { data } = response

        if (data.filesCopied.length >= 1 && data.filesNotCopied.length === 0) {
          toggleLoadingRequesting(false)
          toggleCompletedRequesting(
            t('fileManager.copyFile.dialog.success'),
            true
          )
        } else {
          toggleLoadingRequesting(false)
          setActionType('move')
          setShowFilesCopied(true)

          setFilesSuccess(data.filesCopied.length)
          setFfilesSuccessNames(data.filesCopied.map((file) => file.name))

          setFilesError(data.filesNotCopied.length)
          setFilesErrorNames(data.filesNotCopied.map((file) => file.name))
          refreshFileManager(true)
        }
      } else {
        // Utilizamos el helper para determinar el mensaje de error según el código de estado.
        const errorMsg = getCopyMoveErrorMessage(response.statusCode)
        toggleLoadingRequesting(false)
        toggleErrorRequesting(errorMsg)
      }
    } catch (error) {
      toggleLoadingRequesting(false)
      toggleErrorRequesting(t('fileManager.copyFile.dialog.error'))
      console.error(error)
    }
  }

  async function moveFiles({ companyId, files, target }) {
    toggleLoadingRequesting(true)

    if (files[0].parentId === target.id) {
      toggleLoadingRequesting(false)
      toggleErrorRequesting(t('fileManager.moveFile.dialog.error.sameOrigin'))
      return
    }

    try {
      const response = await categoryService.moveFiles({
        companyId,
        files,
        target,
        token,
      })

      if (response.data && response.statusCode === 200) {
        const { data } = response

        if (data.filesCopied.length >= 1 && data.filesNotCopied.length === 0) {
          toggleLoadingRequesting(false)
          toggleCompletedRequesting(
            t('fileManager.moveFile.dialog.success'),
            true
          )
        } else {
          toggleLoadingRequesting(false)
          setActionType('move')
          setShowFilesCopied(true)

          setFilesSuccess(data.filesCopied.length)
          setFfilesSuccessNames(data.filesCopied.map((file) => file.name))

          setFilesError(data.filesNotCopied.length)
          setFilesErrorNames(data.filesNotCopied.map((file) => file.name))
          refreshFileManager(true)
        }
      } else {
        // Utilizamos el helper para determinar el mensaje de error según el código de estado.
        const errorMsg = getCopyMoveErrorMessage(response.statusCode)
        toggleLoadingRequesting(false)
        toggleErrorRequesting(errorMsg)
      }
    } catch (error) {
      toggleLoadingRequesting(false)
      toggleErrorRequesting(t('fileManager.copyFile.dialog.error'))
      console.error(error)
    }
  }

  async function renameFile({ file, levelUser }) {
    toggleLoadingRequesting(true)

    try {
      const response = await categoryService.renameFile({
        file,
        companyId,
        levelUser,
        token,
      })

      if (response.statusCode === 200) {
        toggleLoadingRequesting(false)
        toggleCompletedRequesting(
          t('fileManager.editFile.dialog.success'),
          true
        )
      } else {
        let errorMsg = t('fileManager.editFile.dialog.error')
        switch (response.statusCode) {
          case 403:
            errorMsg = t('fileManager.editFile.dialog.error.403')
            break
          case 410:
            errorMsg = t('fileManager.editFile.dialog.error.410')
            break
          case 415:
            errorMsg = t('fileManager.editFile.dialog.error.415')
            break
          case 416:
            errorMsg = t('fileManager.editFile.dialog.error.416')
            break
          case 417:
            errorMsg = t('fileManager.editFile.dialog.error.417')
            break
          case 420:
            errorMsg = t('fileManager.editFile.dialog.error.420')
            break
          case 430:
            errorMsg = t('fileManager.editFile.dialog.error.430')
            break
          case 435:
            errorMsg = t('fileManager.editFile.dialog.error.435')
            break
          default:
            errorMsg = t('fileManager.editFile.dialog.error')
            break
        }
        toggleLoadingRequesting(false)
        toggleErrorRequesting(errorMsg)
      }
    } catch (error) {
      toggleLoadingRequesting(false)
      toggleErrorRequesting(t('fileManager.editFile.dialog.error'))
      console.error(error)
    }
  }

  async function deleteFiles({ companyId, files }) {
    toggleLoadingRequesting(true)

    try {
      const foldersToValidate = (Array.isArray(files) ? files : []).filter((f) => f && f.isFile === false)
      if (foldersToValidate.length > 0) {
        const prelimStates = []
        let anyNeedsTrash = false
        const folderChecks = foldersToValidate.map(async (folder) => {
          let hasActive = false
          try {
            if (folder.parentId) {
              // Subcategoría
              const activeResp = await categoryService.getDocumentsBySubcategory(folder.id, companyId, null, null, token, true)
              if (activeResp && Number(activeResp.statusCode) === 200 && Array.isArray(activeResp.data)) {
                hasActive = activeResp.data.length > 0
              }
            } else {
              // Categoría
              const pool = Array.isArray(documentsData) ? documentsData : []
              hasActive = pool.some((d) => belongsToFolder(d, folder))
              if (!hasActive) {
                try {
                  let subs = []
                  const subsResp = await categoryService.getSubcategoriesByCategory(folder.id, companyId, token)
                  if (Array.isArray(subsResp)) subs = subsResp
                  else if (subsResp && Array.isArray(subsResp.data)) subs = subsResp.data
                  const subChecks = subs.map(async (sc) => {
                    const scId = sc?.id || sc?.subcategoryId || sc?.subCategoryId
                    if (!scId) return false
                    try {
                      const resp = await categoryService.getDocumentsBySubcategory(scId, companyId, 0, 0, token, true)
                      return resp && Number(resp.statusCode) === 200 && Array.isArray(resp.data) && resp.data.length > 0
                    } catch (_) {
                      return false
                    }
                  })
                  const results = await Promise.all(subChecks)
                  hasActive = results.some(r => r === true)
                } catch (_) { /* noop */ }
              }
            }
          } catch (_) { /* backend validará */ }

          return { folder, hasActive }
        })

        const results = await Promise.all(folderChecks)
        prelimStates.push(...results)
        anyNeedsTrash = results.some(r => !r.hasActive)

        const anyActiveBlock = prelimStates.some(s => s.hasActive)
        if (!anyNeedsTrash) {
          if (anyActiveBlock) {
            // Construimos blockedFolders con solo activos para mostrar el mensaje existente.
            const blockedFolders = prelimStates.filter(s => s.hasActive).map(s => ({ folder: s.folder, hasActive: true, hasTrash: false }))
            if (blockedFolders.length > 0) {
              let title = t('fileManager.deleteFile.dialog.heading.info.delete')
              const anySub = blockedFolders.some((x) => !!x.folder?.parentId)
              let message = ''
              if (blockedFolders.length === 1) {
                message = anySub
                  ? (t('fileManager.delete.notAvailable.subcategory'))
                  : (t('fileManager.delete.notAvailable.category'))
              } else {
                message = t('fileManager.delete.notAvailable.multiple')
              }
              setDeleteWarningTitle(title)
              setDeleteWarningMessage(message)
              toggleLoadingRequesting(false)
              setShowDeleteWarning(true)
              return
            }
          }
        }

        // Paso 2: Solo ahora obtenemos Papelera si al menos una carpeta no tiene activos.
        let trashDocs = []
        if (anyNeedsTrash) {
          try {
            if (cacheService && typeof cacheService.deleteUrlFromCache === 'function') {
              await Promise.all([
                cacheService.deleteUrlFromCache('/digital-vault-trash').catch(() => { }),
                cacheService.deleteUrlFromCache('/digital-vault-bin').catch(() => { })
              ])
            }

            const [trashResp, binResp] = await Promise.all([
              digitalVaultService.getTrashDocuments(token, timezone).catch(() => null),
              digitalVaultService.getDeletedDocuments(token, timezone).catch(() => null)
            ])

            if (trashResp && Number(trashResp.statusCode) === 200 && trashResp.data) {
              trashDocs = Array.isArray(trashResp.data.documents) ? trashResp.data.documents : []
            }
            if (binResp && Number(binResp.statusCode) === 200 && binResp.data) {
              const more = Array.isArray(binResp.data.documents) ? binResp.data.documents : []
              if (more.length) trashDocs = trashDocs.concat(more)
            }
          } catch (_) {
            trashDocs = []
          }
        }

        const toStr = (v) => (v == null ? '' : String(v))
        const normalize = (s) => (s == null ? '' : String(s))
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .trim()
        const splitIds = (path) => {
          if (!path) return []
          const parts = String(path).split(/[\\/]/).filter(Boolean)
          return parts.map(String)
        }
        // Normaliza un documento para obtener IDs y nombres consistentes (acepta variantes de casing)
        const extractIds = (doc) => {
          if (!doc || typeof doc !== 'object') return {}
          // Aceptar variantes: subCategoryId / subcategoryId
          const subCategoryId = doc.subCategoryId != null ? doc.subCategoryId : doc.subcategoryId
          const categoryId = doc.categoryId != null ? doc.categoryId : (doc.category?.id != null ? doc.category.id : undefined)
          return { categoryId, subCategoryId }
        }

        const belongsToFolder = (doc, folder) => {
          const docPathRaw = toStr(doc.pathBreadcrumb || doc.path || doc.filterPath || '')
          const docPathParts = splitIds(docPathRaw).map(normalize)
          const folderIdStr = String(folder.id)
          const folderNameNorm = normalize(folder.name)
          const isSub = !!folder.parentId
          const ids = extractIds(doc)
          const docCatId = ids.categoryId != null ? String(ids.categoryId) : null
          const docSubId = ids.subCategoryId != null ? String(ids.subCategoryId) : null
          const docCatName = normalize(doc.categoryName || doc.category)
          const docSubName = normalize(doc.subcategoryName || doc.subcategory)

          if (!isSub) {
            // Es una categoría - verificar si el documento pertenece a esta categoría
            if (docCatId && docCatId === folderIdStr) return true
            const docCatParent = doc.categoryParent != null ? String(doc.categoryParent) : null
            if (docCatParent && docCatParent === folderIdStr) return true
            if (docPathParts.includes(folderIdStr)) return true
            if (folderNameNorm && (docCatName === folderNameNorm || docPathParts.includes(folderNameNorm))) return true
            return false
          }

          // Es una subcategoría - verificar si el documento pertenece a esta subcategoría
          if (docSubId && docSubId === folderIdStr) return true
          if (docPathParts.includes(folderIdStr)) return true
          if (folderNameNorm && (docSubName === folderNameNorm || docPathParts.includes(folderNameNorm))) {
            const parentCat = (categoriesData || []).find((c) => String(c.id) === String(folder.parentId))
            if (parentCat && parentCat.name) {
              const parentNameNorm = normalize(parentCat.name)
              if (docCatName) return docCatName === parentNameNorm
              return docPathParts.includes(parentNameNorm)
            }
            return true
          }
          return false
        }

        const trashForCompany = trashDocs.filter((d) => !companyId || (d.companyId == null) || String(d.companyId) === String(companyId))

        const folderStates = []
        for (const folder of foldersToValidate) {
          let hasActive = false
          try {
            if (folder.parentId) {
              const activeResp = await categoryService.getDocumentsBySubcategory(folder.id, companyId, null, null, token, true)
              if (activeResp && Number(activeResp.statusCode) === 200 && Array.isArray(activeResp.data)) {
                hasActive = activeResp.data.length > 0
              }
            } else {
              const pool = Array.isArray(documentsData) ? documentsData : []
              hasActive = pool.some((d) => belongsToFolder(d, folder))
              if (!hasActive) {
                try {
                  let subs = []
                  const subsResp = await categoryService.getSubcategoriesByCategory(folder.id, companyId, token)
                  if (Array.isArray(subsResp)) subs = subsResp
                  else if (subsResp && Array.isArray(subsResp.data)) subs = subsResp.data
                  for (const sc of subs) {
                    const scId = sc?.id || sc?.subcategoryId || sc?.subCategoryId
                    if (!scId) continue
                    try {
                      const resp = await categoryService.getDocumentsBySubcategory(scId, companyId, 0, 0, token, true)
                      if (resp && Number(resp.statusCode) === 200 && Array.isArray(resp.data) && resp.data.length > 0) {
                        hasActive = true
                        break
                      }
                    } catch (_) { /* noop */ }
                  }
                } catch (_) { /* noop */ }
              }
            }
          } catch (_) { /* backend validará */ }

          // Chequeo rápido Papelera
          const quickTrash = trashForCompany.some((d) => belongsToFolder(d, folder))

          if (hasActive) {
            folderStates.push({
              folder,
              hasActive: true,
              hasTrash: false,
              needsDetail: false
            })
          } else if (quickTrash) {
            folderStates.push({
              folder,
              hasActive: false,
              hasTrash: true,
              needsDetail: false
            })
          } else if (trashForCompany.length > 0) {
            folderStates.push({
              folder,
              hasActive: false,
              hasTrash: false,
              needsDetail: true
            })
          } else {
            folderStates.push({
              folder,
              hasActive: false,
              hasTrash: false,
              needsDetail: false
            })
          }
        }

        const unresolved = folderStates.filter((f) => f.needsDetail && !f.hasActive && trashForCompany.length > 0)
        if (unresolved.length > 0) {
          const docIdsChecked = new Set()
          const maxDocs = 15
          const batchSize = 5

          const uniqueDocs = trashForCompany
            .filter(doc => {
              const docId = doc.id || doc.documentId
              if (!docId || docIdsChecked.has(docId)) return false
              docIdsChecked.add(docId)
              return true
            })
            .sort((a, b) => {
              const aHasIds = (a.categoryId || a.subCategoryId) ? 2 : 0
              const bHasIds = (b.categoryId || b.subCategoryId) ? 2 : 0
              return bHasIds - aHasIds
            })
            .slice(0, maxDocs)

          if (uniqueDocs.length === 0) {
            unresolved.forEach(state => {
              state.needsDetail = false
              state.hasTrash = false
            })
          } else {

            for (let i = 0; i < uniqueDocs.length; i += batchSize) {
              if (unresolved.every((u) => !u.needsDetail)) {
                break
              }

              const batch = uniqueDocs.slice(i, i + batchSize)
              const promises = batch.map(trashDoc => {
                const docId = trashDoc.id || trashDoc.documentId

                const canQuickCheck = trashDoc.categoryId || trashDoc.subCategoryId
                if (canQuickCheck) {
                  for (const state of unresolved) {
                    if (!state.needsDetail) continue
                    const folder = state.folder

                    if (folder.parentId) {
                      if (trashDoc.subCategoryId != null && String(trashDoc.subCategoryId) === String(folder.id)) {
                        state.hasTrash = true
                        state.needsDetail = false
                      }
                    } else {
                      if (trashDoc.categoryId != null && String(trashDoc.categoryId) === String(folder.id)) {
                        state.hasTrash = true
                        state.needsDetail = false
                      }
                    }
                  }

                  if (unresolved.every(u => !u.needsDetail)) {
                    return Promise.resolve()
                  }
                }

                return categoryService.getDocumentVaultData(docId, timezone, token)
                  .then(detailResp => {
                    if (detailResp && Number(detailResp.statusCode) === 200) {
                      const fullDoc = detailResp?.data?.document || detailResp?.data?.data?.document || null
                      if (fullDoc) {
                        const enriched = {
                          ...fullDoc,
                          categoryId: fullDoc.categoryId != null ? fullDoc.categoryId : fullDoc.category_id,
                          subCategoryId: fullDoc.subCategoryId != null ? fullDoc.subCategoryId : fullDoc.subcategoryId
                        }

                        for (const state of unresolved) {
                          if (!state.needsDetail) continue
                          const folder = state.folder

                          if (folder.parentId) {
                            if (enriched.subCategoryId != null && String(enriched.subCategoryId) === String(folder.id)) {
                              state.hasTrash = true
                              state.needsDetail = false
                            }
                          } else {
                            if (enriched.categoryId != null && String(enriched.categoryId) === String(folder.id)) {
                              state.hasTrash = true
                              state.needsDetail = false
                            }
                          }
                        }
                      }
                    }
                  })
                  .catch(err => {
                    console.error(err)
                  })
              })
              await Promise.all(promises)
            }
          }
        }

        const blockedFolders = folderStates.filter((s) => s.hasActive || s.hasTrash)

        if (blockedFolders.length > 0) {
          let title = t('fileManager.deleteFile.dialog.heading.info.delete')
          const anySub = blockedFolders.some((x) => !!x.folder?.parentId)
          let message = ''
          if (blockedFolders.length === 1) {
            message = anySub
              ? (t('fileManager.delete.notAvailable.subcategory'))
              : (t('fileManager.delete.notAvailable.category'))
          } else {
            message = t('fileManager.delete.notAvailable.multiple')
          }

          setDeleteWarningTitle(title)
          setDeleteWarningMessage(message)
          toggleLoadingRequesting(false)
          setShowDeleteWarning(true)
          return
        }
      }

      const keyOf = (f) => `${f && f.id != null ? f.id : 'nil'}::${f && f.name ? f.name : ''}::${f && f.isFile ? 'file' : 'folder'}`
      const seen = new Set()
      const unique = []
      for (const f of (Array.isArray(files) ? files : [])) {
        if (!f) continue
        const k = keyOf(f)
        if (seen.has(k)) continue
        seen.add(k)
        unique.push(f)
      }

      const withLevel = unique.map((item) => {
        try {
          const level = Math.max(1, (checkDepth({ str: item.filterPath }) || 2) - 1)
          return {
            ...item,
            id: item.id,
            name: item.name,
            isFile: !!item.isFile,
            parentId: item.parentId,
            level,
            type: item.type || item.infoType,
            infoType: item.infoType,
          }
        } catch (_) {
          return {
            ...item,
            id: item.id,
            name: item.name,
            isFile: !!item.isFile,
            parentId: item.parentId,
            level: 2,
            type: item.type || item.infoType,
            infoType: item.infoType,
          }
        }
      })

      const filesOnly = withLevel.filter((i) => i?.isFile)
      const foldersOnly = withLevel.filter((i) => !i?.isFile)

      let agg = { filesDeleted: [], filesNotDeleted: [] }
      const runDelete = async (batch) => {
        if (!batch || batch.length === 0) return { ok: true }
        const resp = await categoryService.deleteFiles({ companyId, files: batch, token })
        if (resp?.statusCode === 200 && resp?.data) {
          const d = resp.data
          agg.filesDeleted = agg.filesDeleted.concat(d.filesDeleted || [])
          agg.filesNotDeleted = agg.filesNotDeleted.concat(d.filesNotDeleted || [])
          return { ok: true }
        }
        return { ok: false, resp }
      }

      const r1 = await runDelete(filesOnly)
      if (!r1.ok) {
        const response = r1.resp || {}
        let errorMsg = t('fileManager.deleteFile.dialog.error')
        switch (response.statusCode) {
          case 410:
            errorMsg = t('fileManager.copyFile.dialog.error.410')
            break
          case 415:
            errorMsg = t('fileManager.deleteFile.dialog.error.415')
            break
          case 416:
            errorMsg = t('fileManager.deleteFile.dialog.error.416')
            break
          case 418:
            errorMsg = t('fileManager.deleteFile.dialog.error.418')
            break
          case 420:
            errorMsg = t('fileManager.deleteFile.dialog.error.420')
            break
          case 430:
            errorMsg = t('fileManager.deleteFile.dialog.error.430')
            break
          case 431:
            errorMsg = t('fileManager.deleteFile.dialog.error.431')
            break
          case 432:
            errorMsg = t('fileManager.deleteFile.dialog.error.432')
            break
          case 440:
            errorMsg = t('fileManager.deleteFile.dialog.error.432')
            break
          default:
            errorMsg = t('fileManager.deleteFile.dialog.error')
            break
        }
        toggleLoadingRequesting(false)
        toggleErrorRequesting(errorMsg)
        return
      }

      const r2 = await runDelete(foldersOnly)
      if (!r2.ok) {
        const response = r2.resp || {}
        let errorMsg = t('fileManager.deleteFile.dialog.error')
        switch (response.statusCode) {
          case 410:
            errorMsg = t('fileManager.copyFile.dialog.error.410')
            break
          case 415:
            errorMsg = t('fileManager.deleteFile.dialog.error.415')
            break
          case 416:
            errorMsg = t('fileManager.deleteFile.dialog.error.416')
            break
          case 418:
            errorMsg = t('fileManager.deleteFile.dialog.error.418')
            break
          case 420:
            errorMsg = t('fileManager.deleteFile.dialog.error.420')
            break
          case 430:
            errorMsg = t('fileManager.deleteFile.dialog.error.430')
            break
          case 431:
            errorMsg = t('fileManager.deleteFile.dialog.error.431')
            break
          case 432:
            errorMsg = t('fileManager.deleteFile.dialog.error.432')
            break
          case 440:
            errorMsg = t('fileManager.deleteFile.dialog.error.432')
            break
          default:
            errorMsg = t('fileManager.deleteFile.dialog.error')
            break
        }
        toggleLoadingRequesting(false)
        toggleErrorRequesting(errorMsg)
        return
      }
      const byId = new Map(withLevel.map((it) => [it.id, it]))
      const byName = new Map()
      for (const it of withLevel) {
        if (!it || !it.name) continue
        if (!byName.has(it.name)) byName.set(it.name, it)
      }

      const enrich = (arr) => (arr || []).map((nd) => {
        if (!nd) return nd
        const kId = nd.id != null ? nd.id : undefined
        let match = kId != null ? byId.get(kId) : undefined
        if (!match && nd.name) match = byName.get(nd.name)
        return match ? { ...nd, ...match } : nd
      })
      const notDeletedEnriched = enrich(agg.filesNotDeleted)
      const blockedFolders = notDeletedEnriched.filter((f) => f && f.isFile === false)

      if (blockedFolders.length > 0) {
        let title = t('fileManager.deleteFile.dialog.heading.info.delete')
        let message = ''

        if (blockedFolders.length === 1) {
          const only = blockedFolders[0]
          const isSub = !!only?.parentId
          message = isSub
            ? (t('fileManager.delete.notAvailable.subcategory'))
            : (t('fileManager.delete.notAvailable.category'))
        } else {
          message = t('fileManager.delete.notAvailable.multiple')
        }

        setDeleteWarningTitle(title)
        setDeleteWarningMessage(message)
        toggleLoadingRequesting(false)
        setShowDeleteWarning(true)
        refreshFileManager(true)
        return
      }

      if (agg.filesDeleted.length >= 1 && agg.filesNotDeleted.length === 0) {
        toggleLoadingRequesting(false)
        toggleCompletedRequesting(
          t('fileManager.deleteFile.dialog.success'),
          true,
          agg.filesDeleted[0].id
        )
      } else {
        toggleLoadingRequesting(false)
        setActionType('delete')
        setShowFilesCopied(true)

        setFilesSuccess(agg.filesDeleted.length)
        setFfilesSuccessNames(agg.filesDeleted.map((file) => file.name))

        setFilesError(agg.filesNotDeleted.length)
        setFilesErrorNames(agg.filesNotDeleted.map((file) => file.name))
        refreshFileManager(true)
      }
    } catch (error) {
      toggleLoadingRequesting(false)
      toggleErrorRequesting(t('fileManager.deleteFile.dialog.error'))
      console.error(error)
    }
  }

  async function downloadFile({ file, companyId }) {
    toggleLoadingRequesting(true)

    try {
      const response = await categoryService.downloadFile({
        file,
        companyId,
        token,
      })

      if (response.data && response.statusCode === 200) {
        toggleLoadingRequesting(false)
        window.location.assign(response.data)
        toggleCompletedRequesting(
          t('fileManager.downloadFile.dialog.success.browser'),
          false
        )
      } else if (
        (!response.data || response.data === '') &&
        response.statusCode === 200
      ) {
        toggleLoadingRequesting(false)
        toggleCompletedRequesting(
          t('fileManager.downloadFile.dialog.success.email'),
          false
        )
      } else {
        let errorMsg = t('fileManager.downloadFile.dialog.error')
        switch (response.statusCode) {
          case 410:
            errorMsg = t('fileManager.renameFile.dialog.error.410')
            break
          case 415:
            errorMsg = t('fileManager.renameFile.dialog.error.415')
            break
          case 416:
            errorMsg = t('fileManager.renameFile.dialog.error.416')
            break
          case 417:
            errorMsg = t('fileManager.renameFile.dialog.error.417')
            break
          case 432:
            errorMsg = t('fileManager.deleteFile.dialog.error.432')
            break
          default:
            errorMsg = t('fileManager.downloadFile.dialog.error')
            break
        }
        toggleLoadingRequesting(false)
        toggleErrorRequesting(errorMsg)
      }
    } catch (error) {
      toggleLoadingRequesting(false)
      toggleErrorRequesting(t('fileManager.downloadFile.dialog.error'))
      console.error(error)
    }
  }

  function addNewFile() {
    if (!fileObj.current) return
    if (fileObj.current.currentItemText === `${process.env.REACT_APP_LOGICAL_CATEGORY_MY_RECEIPTS}` || fileObj.current.currentItemText === `${process.env.REACT_APP_LOGICAL_CATEGORY_MY_SHARED}`) {
      toggleErrorRequesting(t('fileManager.logical.noAddFile'))
      return
    }

    const pathIds = fileObj.current.path.split('/')
    const categoryId = pathIds[1]
    const subPath = pathIds.slice(2, -1).join(',')

    const company = companies.filter((c) => c.company.id === companyId)[0]

    let newParams = new URLSearchParams()

    if (companyId) newParams.append('companyId', companyId)
    if (categoryId) newParams.append('categoryId', categoryId)
    if (subPath) newParams.append('path', subPath)

    history.push({
      pathname: `/digital-vault-upload`,
      search: newParams.toString(),
      state: {
        companyId,
        fileManagerPath: fileObj.current.path,
        selectedCategory: categoryId,
        selectedCompany: company,
        subcategoryPath: subPath
      },
    })
  }

  function depthLevelActions({
    filterPath,
    fileDetails,
    addFile = false,
    changeBtnStr = false,
    updateBreadcrumbs = false,
  }) {
    const depthLevel = checkDepth({ str: filterPath })

    if (addFile) {
      setShowAddFileBtn(depthLevel >= 3)
    }

    if (changeBtnStr) {
      setStrBtn(
        depthLevel >= 2
          ? t('fileManager.toolbar.newSubcategory')
          : t('fileManager.toolbar.newCategory')
      )
    }
  }

  /**
   * @param {import('@syncfusion/ej2-react-filemanager').MenuOpenEventArgs} args
   * @param {string} idSuffix
   * @param {string} css
   */
  const setIconCss = (args, idSuffix, css) => {
    args.items.forEach((item) => {
      if (item.id === fileObj.current.element.id + idSuffix) {
        item.iconCss = css
      }
    })
  }

  /**
   * @param {import('@syncfusion/ej2-react-filemanager').MenuOpenEventArgs} args
   * @param {string} idSuffix
   * @param {string} text
   */
  const setMenuText = (args, idSuffix, text) => {
    args.items.forEach((item) => {
      if (item.id === fileObj.current.element.id + idSuffix) {
        item.text = text
      }
    })
  }

  /** @param {import('@syncfusion/ej2-react-filemanager').MenuOpenEventArgs} args  */
  function onMenuOpen(args) {
    const fileDetail = args.fileDetails[0]

    setIconCss(args, '_cm_cut', 'e-icons e-move')
    setMenuText(args, '_cm_cut', t('fileManager.contextMenu.move'))
    setIconCss(args, '_cm_analyze', 'e-icons e-analyze')

    // If a logical file is selected, disable the options.
    const logicalFiles = args.fileDetails ? args.fileDetails.some(file => { return file.logical }) : false
    if (logicalFiles) {
      fileObj.current.enableMenuItems(['Open', 'Details', 'Download'])
      setIconCss(args, '_cm_download', 'e-icons e-fe-download')
      setIconCss(args, '_cm_open', 'e-icons e-fe-open')
      setIconCss(args, '_cm_details', 'e-icons e-fe-details')
      setMenuText(args, '_cm_details', t('fileManager.contextMenu.categoryDetails'))

      fileObj.current.disableMenuItems([
        'Delete',
        'Rename',
        'Copy',
        'Cut',
        'Paste',
        'NewFolder'
      ])

      setIconCss(args, '_cm_delete', 'e-icons e-delete-disabled')
      setIconCss(args, '_cm_rename', 'e-icons e-rename-disabled')
      setIconCss(args, '_cm_copy', 'e-icons e-copy-disabled')
      setIconCss(args, '_cm_cut', 'e-icons e-move-disabled')
      setIconCss(args, '_cm_paste', 'e-icons e-paste-disabled')

      if (fileDetail.key && fileDetail.key === process.env.REACT_APP_LOGICAL_CATEGORY_UNASSIGNED_SIGNATURE_KEY) {
        fileObj.current.enableMenuItems(['Cut'])
        setIconCss(args, '_cm_cut', 'e-icons e-move')
      }
      return
    }

    // Several category folders selected
    if (
      args.menuType === 'folder' &&
      !fileDetail.parentId &&
      fileObj.current.selectedItems.length > 1
    ) {
      fileObj.current.disableMenuItems([
        'Download',
        'Rename',
        'Cut',
        'Open',
        'Details'
      ])
      fileObj.current.enableMenuItems(['Copy'])
      setIconCss(args, '_cm_rename', 'e-icons e-rename-disabled')
      setIconCss(args, '_cm_download', 'e-icons e-download-disabled')
      setIconCss(args, '_cm_cut', 'e-icons e-move-disabled')
      setIconCss(args, '_cm_open', 'e-icons e-open-disabled')
      setIconCss(args, '_cm_details', 'e-icons e-details-disabled')
      setMenuText(args, '_cm_details', t('fileManager.contextMenu.categoryDetails'))

      if (!permission.category.copy) {
        fileObj.current.disableMenuItems(['Copy', 'Cut'])
        setIconCss(args, '_cm_copy', 'e-icons e-copy-disabled')
        setIconCss(args, '_cm_cut', 'e-icons e-move-disabled')
      }

      if (!permission.category.delete) {
        fileObj.current.disableMenuItems(['Delete'])
        setIconCss(args, '_cm_delete', 'e-icons e-delete-disabled')
      }

      if (!permission.category.rename) {
        fileObj.current.disableMenuItems(['Rename'])
        setIconCss(args, '_cm_rename', 'e-icons e-rename-disabled')
      }

      if (!permission.category.download) {
        fileObj.current.disableMenuItems(['Download'])
        setIconCss(args, '_cm_download', 'e-icons e-download-disabled')
      }

      if (!permission.category.open) {
        fileObj.current.disableMenuItems(['Open'])
        setIconCss(args, '_cm_open', 'e-icons e-open-disabled')
      }
      return
    }

    // Company main node
    if (
      args.menuType === 'folder' &&
      !fileDetail.filterPath &&
      !fileDetail.parentId
    ) {
      fileObj.current.disableMenuItems(['Download', 'Open'])
      setIconCss(args, '_cm_open', 'e-icons e-open-disabled')
      setIconCss(args, '_cm_copy', 'e-icons e-copy-disabled')
      setIconCss(args, '_cm_paste', 'e-icons e-paste-disabled')
      setIconCss(args, '_cm_cut', 'e-icons e-move-disabled')
      setIconCss(args, '_cm_delete', 'e-icons e-delete-disabled')
      setIconCss(args, '_cm_download', 'e-icons e-download-disabled')
      setIconCss(args, '_cm_rename', 'e-icons e-rename-disabled')
      setMenuText(args, '_cm_details', t('fileManager.contextMenu.companyDetails'))
      return
    }

    // One category folder selected
    if (
      (args.menuType === 'folder' &&
        !fileDetail.parentId &&
        fileObj.current.selectedItems.length === 0) ||
      (args.menuType === 'folder' &&
        !fileDetail.parentId &&
        fileObj.current.selectedItems.length === 1)
    ) {
      fileObj.current.disableMenuItems(['Paste', 'Cut'])
      setIconCss(args, '_cm_paste', 'e-icons e-paste-disabled')
      setIconCss(args, '_cm_cut', 'e-icons e-move-disabled')

      fileObj.current.enableMenuItems([
        'Download',
        'Rename',
        'Copy',
        'Open',
        'Details'
      ])

      setIconCss(args, '_cm_open', 'e-icons e-fe-open')
      setIconCss(args, '_cm_details', 'e-icons e-fe-details')
      setMenuText(args, '_cm_details', t('fileManager.contextMenu.categoryDetails'))

      if (!permission.category.copy) {
        fileObj.current.disableMenuItems(['Copy', 'Cut'])
        setIconCss(args, '_cm_copy', 'e-icons e-copy-disabled')
        setIconCss(args, '_cm_cut', 'e-icons e-move-disabled')
      }

      if (!permission.category.delete) {
        fileObj.current.disableMenuItems(['Delete'])
        setIconCss(args, '_cm_delete', 'e-icons e-delete-disabled')
      }

      if (!permission.category.rename) {
        fileObj.current.disableMenuItems(['Rename'])
        setIconCss(args, '_cm_rename', 'e-icons e-rename-disabled')
      }

      if (!permission.category.download) {
        fileObj.current.disableMenuItems(['Download'])
        setIconCss(args, '_cm_download', 'e-icons e-download-disabled')
      }

      if (!permission.category.open) {
        fileObj.current.disableMenuItems(['Open'])
        setIconCss(args, '_cm_open', 'e-icons e-open-disabled')
      }
      return
    }

    // Several subcategory folder selected
    if (
      args.menuType === 'folder' &&
      fileDetail.parentId !== 0 &&
      fileObj.current.selectedItems.length > 1
    ) {
      fileObj.current.disableMenuItems([
        'Download',
        'Rename',
        'Open',
        'Details'
      ])
      setIconCss(args, '_cm_rename', 'e-icons e-rename-disabled')
      setIconCss(args, '_cm_download', 'e-icons e-download-disabled')
      setIconCss(args, '_cm_open', 'e-icons e-open-disabled')
      setIconCss(args, '_cm_details', 'e-icons e-details-disabled')
      setMenuText(args,
        '_cm_details',
        t('fileManager.contextMenu.subcategoryDetails')
      )

      if (!permission.subcategory.copy) {
        fileObj.current.disableMenuItems(['Copy', 'Cut'])
        setIconCss(args, '_cm_copy', 'e-icons e-copy-disabled')
        setIconCss(args, '_cm_cut', 'e-icons e-move-disabled')
      }

      if (!permission.subcategory.delete) {
        fileObj.current.disableMenuItems(['Delete'])
        setIconCss(args, '_cm_delete', 'e-icons e-delete-disabled')
      }

      if (!permission.subcategory.rename) {
        fileObj.current.disableMenuItems(['Rename'])
        setIconCss(args, '_cm_rename', 'e-icons e-rename-disabled')
      }

      if (!permission.subcategory.download) {
        fileObj.current.disableMenuItems(['Download'])
        setIconCss(args, '_cm_download', 'e-icons e-download-disabled')
      }

      if (!permission.subcategory.open) {
        fileObj.current.disableMenuItems(['Open'])
        setIconCss(args, '_cm_open', 'e-icons e-open-disabled')
      }
      return
    }

    // One subcategory folder selected
    if (
      (args.menuType === 'folder' &&
        fileDetail.parentId !== 0 &&
        fileObj.current.selectedItems.length === 0) ||
      (args.menuType === 'folder' &&
        fileDetail.parentId !== 0 &&
        fileObj.current.selectedItems.length === 1)
    ) {
      fileObj.current.enableMenuItems([
        'Download',
        'Rename',
        'Copy',
        'Cut',
        'Open',
        'Details'
      ])
      setIconCss(args, '_cm_rename', 'e-icons e-fe-rename')
      setIconCss(args, '_cm_download', 'e-icons e-fe-download')
      setIconCss(args, '_cm_open', 'e-icons e-fe-open')
      setIconCss(args, '_cm_details', 'e-icons e-fe-details')
      setMenuText(args,
        '_cm_details',
        t('fileManager.contextMenu.subcategoryDetails')
      )

      if (!permission.subcategory.copy) {
        fileObj.current.disableMenuItems(['Copy', 'Cut'])
        setIconCss(args, '_cm_copy', 'e-icons e-copy-disabled')
        setIconCss(args, '_cm_cut', 'e-icons e-move-disabled')
      }

      if (!permission.subcategory.delete) {
        fileObj.current.disableMenuItems(['Delete'])
        setIconCss(args, '_cm_delete', 'e-icons e-delete-disabled')
      }

      if (!permission.subcategory.rename) {
        fileObj.current.disableMenuItems(['Rename'])
        setIconCss(args, '_cm_rename', 'e-icons e-rename-disabled')
      }

      if (!permission.subcategory.download) {
        fileObj.current.disableMenuItems(['Download'])
        setIconCss(args, '_cm_download', 'e-icons e-download-disabled')
      }

      if (!permission.subcategory.open) {
        fileObj.current.disableMenuItems(['Open'])
        setIconCss(args, '_cm_open', 'e-icons e-open-disabled')
      }
      return
    }

    // Several folder or files selected
    if (fileDetail.isFile && fileObj.current.selectedItems.length > 1) {
      fileObj.current.disableMenuItems([
        'Download',
        'Rename',
        'Open',
        'Details',
        'Analyze'
      ])
      setIconCss(args, '_cm_rename', 'e-icons e-rename-disabled')
      setIconCss(args, '_cm_download', 'e-icons e-download-disabled')
      setIconCss(args, '_cm_open', 'e-icons e-open-disabled')
      setIconCss(args, '_cm_details', 'e-icons e-details-disabled')
      setMenuText(args, '_cm_details', t('fileManager.contextMenu.fileDetails'))
      setIconCss(args, '_cm_analyze', 'e-icons e-analyze-disabled')

      if (!permission.subcategory.copy) {
        fileObj.current.disableMenuItems(['Copy', 'Cut'])
        setIconCss(args, '_cm_copy', 'e-icons e-copy-disabled')
        setIconCss(args, '_cm_cut', 'e-icons e-move-disabled')
      }

      if (!permission.subcategory.delete) {
        fileObj.current.disableMenuItems(['Delete'])
        setIconCss(args, '_cm_delete', 'e-icons e-delete-disabled')
      }

      if (!permission.subcategory.rename) {
        fileObj.current.disableMenuItems(['Rename'])
        setIconCss(args, '_cm_rename', 'e-icons e-rename-disabled')
      }

      if (!permission.subcategory.download) {
        fileObj.current.disableMenuItems(['Download'])
        setIconCss(args, '_cm_download', 'e-icons e-download-disabled')
      }

      if (!permission.subcategory.open) {
        fileObj.current.disableMenuItems(['Open'])
        setIconCss(args, '_cm_open', 'e-icons e-open-disabled')
      }
      return
    }

    // One file selected
    if (fileDetail.isFile && fileObj.current.selectedItems.length === 1) {
      fileObj.current.enableMenuItems([
        'Download',
        'Rename',
        'Open',
        'Details',
        'Analyze'
      ])
      setIconCss(args, '_cm_rename', 'e-icons e-fe-rename')
      setIconCss(args, '_cm_download', 'e-icons e-fe-download')
      setIconCss(args, '_cm_open', 'e-icons e-fe-open')
      setIconCss(args, '_cm_details', 'e-icons e-fe-details')
      setIconCss(args, '_cm_analyze', 'e-icons e-analyze')
      setMenuText(args, '_cm_details', t('fileManager.contextMenu.fileDetails'))

      if (!permission.subcategory.copy) {
        fileObj.current.disableMenuItems(['Copy', 'Cut'])
        setIconCss(args, '_cm_copy', 'e-icons e-copy-disabled')
        setIconCss(args, '_cm_cut', 'e-icons e-move-disabled')
      }

      if (!permission.subcategory.delete) {
        fileObj.current.disableMenuItems(['Delete'])
        setIconCss(args, '_cm_delete', 'e-icons e-delete-disabled')
      }

      if (!permission.subcategory.rename) {
        fileObj.current.disableMenuItems(['Rename'])
        setIconCss(args, '_cm_rename', 'e-icons e-rename-disabled')
      }

      if (!permission.subcategory.download) {
        fileObj.current.disableMenuItems(['Download'])
        setIconCss(args, '_cm_download', 'e-icons e-download-disabled')
      }

      if (!permission.subcategory.open) {
        fileObj.current.disableMenuItems(['Open'])
        setIconCss(args, '_cm_open', 'e-icons e-open-disabled')
      }

      if ((fileDetail.statusIA !== t('fileManager.listView.header.status.ia.no.analized'))) {
        fileObj.current.disableMenuItems(['Analyze'])
        setIconCss(args, '_cm_analyze', 'e-icons e-analyze-disabled')
      }

      if (fileObj.current.pathNames[2]) {

        // Mis recibidos
        if (fileObj.current.pathNames[2] === `${process.env.REACT_APP_LOGICAL_CATEGORY_MY_RECEIPTS}`) {
          fileObj.current.enableMenuItems(['Open', 'Details', 'Download'])
          setIconCss(args, '_cm_open', 'e-icons e-fe-open')
          setIconCss(args, '_cm_details', 'e-icons e-fe-details')
          setIconCss(args, '_cm_download', 'e-icons e-fe-download')
          setMenuText(args, '_cm_details', t('fileManager.contextMenu.fileDetails'))

          fileObj.current.disableMenuItems([
            'Delete',
            'Analyze',
            'Rename',
            'Copy',
            'Cut',
            'Paste'
          ])

          setIconCss(args, '_cm_delete', 'e-icons e-delete-disabled')
          setIconCss(args, '_cm_analyze', 'e-icons e-analyze-disabled')
          setIconCss(args, '_cm_rename', 'e-icons e-rename-disabled')
          setIconCss(args, '_cm_copy', 'e-icons e-copy-disabled')
          setIconCss(args, '_cm_cut', 'e-icons e-move-disabled')
          setIconCss(args, '_cm_paste', 'e-icons e-paste-disabled')
          return
        }

        // Mis compartidos
        if (fileObj.current.pathNames[2] === `${process.env.REACT_APP_LOGICAL_CATEGORY_MY_SHARED}`) {
          fileObj.current.enableMenuItems(['Open', 'Details', 'Rename', 'Download'])
          setIconCss(args, '_cm_open', 'e-icons e-fe-open')
          setIconCss(args, '_cm_details', 'e-icons e-fe-details')
          setIconCss(args, '_cm_rename', 'e-icons e-fe-rename')
          setIconCss(args, '_cm_download', 'e-icons e-download')
          setMenuText(args, '_cm_details', t('fileManager.contextMenu.fileDetails'))

          fileObj.current.disableMenuItems([
            'Delete',
            'Analyze',
            'Copy',
            'Cut',
            'Paste'
          ])

          setIconCss(args, '_cm_delete', 'e-icons e-delete-disabled')
          setIconCss(args, '_cm_analyze', 'e-icons e-analyze-disabled')
          setIconCss(args, '_cm_copy', 'e-icons e-copy-disabled')
          setIconCss(args, '_cm_cut', 'e-icons e-move-disabled')
          setIconCss(args, '_cm_paste', 'e-icons e-paste-disabled')
          return
        }
      }
      return
    }
  }

  function checkPermission({ action, type, str, fileDetails }) {
    const level = checkDepth({ str })

    const permissionMap = {
      category: {
        create: permission.category.create,
        copy: permission.category.copy,
        download: permission.category.download,
        delete: permission.category.delete,
        open: permission.category.open,
        rename: permission.category.rename,
      },
      subcategory: {
        create: permission.subcategory.create,
        copy: permission.subcategory.copy,
        download: permission.subcategory.download,
        delete: permission.subcategory.delete,
        open: permission.subcategory.open,
        rename: permission.subcategory.rename,
      }
    }

    let hasPermission = false
    let errorMsg = t('fileManager.permission')

    if (action === 'open') {
      if (fileDetails.name === `${process.env.REACT_APP_LOGICAL_CATEGORY_SHARED}` || fileDetails.name === `${process.env.REACT_APP_LOGICAL_CATEGORY_MY_SHARED}` || fileDetails.name === `${process.env.REACT_APP_LOGICAL_CATEGORY_MY_RECEIPTS}`) {
        hasPermission = true
        return hasPermission
      }
    }

    if (type === 'folder') {
      if (level === 1 && action === 'open') {
        hasPermission = true
      } else if (level === 2) {
        hasPermission = permissionMap.category[action]
        errorMsg = t(`fileManager.permission.category.${action}`)
      } else if (level >= 3) {
        hasPermission = permissionMap.subcategory[action]
        errorMsg = t(`fileManager.permission.subcategory.${action}`)
      }
    } else if (type === 'file') {
      hasPermission = permissionMap.subcategory[action]
      errorMsg = t(`fileManager.permission.file.${action}`)
    }

    if (!hasPermission && errorMsg) {
      toggleErrorRequesting(errorMsg)
    }

    return hasPermission
  }

  const onCreated = () => {
    const fileManagerElement = document.getElementById(
      'DigitalVault_filemanager'
    )
    const largeicon_view = document.getElementById(
      'DigitalVault_filemanager_layout'
    )
    const details_view = document.getElementById(
      'DigitalVault_filemanager_grid'
    )

    fileManagerElement.addEventListener('keyup', keyUpHandler, true)
    largeicon_view.addEventListener('keyup', keyUpHandler, true)
    details_view.addEventListener('keyup', keyUpHandler, true)

    try {
      if (fileObj && fileObj.current && fileObj.current.showToolbarItems) {
        fileObj.current.showToolbarItems(['Download'])
        fileObj.current.disableToolbarItems(['Download'])
      }
    } catch (e) {
    }
  }

  const keyUpHandler = (e) => {
    // Always use the latest selection info
    const currentPath = pathRef.current
    const currentIsFile = isFile.current

    // Map key codes to actions
    const keyActionMap = {
      67: 'copy',      // Ctrl+C
      86: 'copy',      // Ctrl+V
      88: 'copy',      // Ctrl+X 
      68: 'download',  // Ctrl+D
    }

    // Only handle if a mapped shortcut is pressed
    const action = keyActionMap[e.which]
    if ((e.ctrlKey || e.metaKey) && action) {
      // Determine type for permission check
      const type = currentIsFile ? 'file' : 'folder'

      if (!checkPermission({
        action,
        type,
        str: currentPath,
      })) {
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        return false
      }
    }
  }

  async function onFileOpen(args) {
    const { fileDetails } = args
    if (
      !checkPermission({
        action: 'open',
        type: fileDetails.isFile ? 'file' : 'folder',
        str: fileDetails.filterPath,
        fileDetails
      })
    ) {
      args.cancel = true
      return
    }


    // Set breadcrumb items to show forward in the document detail
    if (fileDetails.isFile) {
      if (fileObj.current.pathNames.length > 1 && fileObj.current.path) {
        const pathNamesArray = fileObj.current.pathNames
        const pathIdsString = fileObj.current.path

        const pathIds = pathIdsString.split('/')
        pathIds.pop()

        const items = pathNamesArray.map((_, index) => ({
          id: pathIds[index],
          title: pathNamesArray[index],
        }))

        setBreadcrumbItems(items)
      }
    }

    // If it's a file selected, then we update the document id to change to the document view
    if (fileDetails.isFile && fileDetails.id) {
      setDocumentId(fileDetails.id)
      setSubcategoryId(fileDetails.parentId)
    }

    if (
      fileDetails &&
      fileDetails.id &&
      fileDetails.parentId &&
      !fileDetails.isFile
    ) {
      await getDocumentsBySubcategory(fileDetails.id, false, fileDetails)
    }

    // Update the toolbar buttons depending on the file manager depth
    if (!fileDetails.isFile && !fileDetails.key) {
      depthLevelActions({
        filterPath: fileDetails.filterPath,
        addFile: true,
        changeBtnStr: true,
      })
    }

    if (fileDetails.logical || fileDetails.key === 'shared' || fileDetails.key === 'myShared' || fileDetails.key === 'myReceipts') {
      fileObj.current.disableToolbarItems(['NewFolder'])
    }

  }

  function onFileSelect(args) {
    const { fileDetails, selectedItems } = args

    if (!fileObj.current || !fileObj.current.selectedItems) {
      return
    }

    const multipleImmediate = fileObj.current && Array.isArray(fileObj.current.selectedItems) && fileObj.current.selectedItems.length > 1
    if (multipleImmediate) {
      if (fileObj.current && fileObj.current.toolbarModule && fileObj.current.toolbarModule.items) {
        fileObj.current.toolbarModule.items.forEach((item) => {
          if (item.id === 'DigitalVault_filemanager_tb_analyzefile') item.visible = false
        })
      }
    }

    if (!fileDetails) {
      const remaining = (selectedItems && Array.isArray(selectedItems))
        ? selectedItems.length
        : (fileObj.current && fileObj.current.selectedItems
          ? fileObj.current.selectedItems.length
          : 0)
      if (remaining === 0) {
        if (fileObj.current && fileObj.current.toolbarModule && fileObj.current.toolbarModule.items) {
          fileObj.current.toolbarModule.items.forEach((item) => {
            if (item.id === 'DigitalVault_filemanager_tb_analyzefile') {
              item.visible = false
            }
          })
        }
        return
      }
    }
    if (!fileDetails) return
    pathRef.current = fileDetails.filterPath
    isFile.current = fileDetails.isFile
    if (fileObj.current) fileObj.current.documentSelected = null

    const multiSelection = fileObj.current && fileObj.current.selectedItems && fileObj.current.selectedItems.length > 1

    if (fileDetails.type === 'folder') {
      if (fileDetails.key === 'shared') {
        fileObj.current.disableToolbarItems(['NewFolder', 'Copy', 'Paste', 'Download'])
      } else if (fileDetails.key === 'myShared') {
        fileObj.current.disableToolbarItems(['NewFolder', 'Copy', 'Paste', 'Download'])
      } else if (fileDetails.key === 'myReceipts') {
        fileObj.current.disableToolbarItems(['NewFolder', 'Copy', 'Paste', 'Download'])
      } else {
        if (fileObj.current.selectedItems.length > 1) {
          fileObj.current.enableToolbarItems(['Download'])
        }
      }

      if (fileDetails.logical) {
        fileObj.current.disableToolbarItems(['Copy', 'Delete', 'Rename'])
      }

      // Category selected
      if (fileDetails.parentId === 0) {
        if (!permission.category.copy) {
          fileObj.current.disableToolbarItems(['Copy'])
        }
        if (!permission.category.download) {
          fileObj.current.disableToolbarItems(['Download'])
        }
        if (!permission.category.delete) {
          fileObj.current.disableToolbarItems(['Delete'])
        }
        if (!permission.category.rename) {
          fileObj.current.disableToolbarItems(['Rename'])
        }
      }

      // Subcategory selected
      if (fileDetails.parentId !== 0) {
        if (!permission.subcategory.copy) {
          fileObj.current.disableToolbarItems(['Copy'])
        }
        if (!permission.subcategory.download) {
          fileObj.current.disableToolbarItems(['Download'])
        }
        if (!permission.subcategory.delete) {
          fileObj.current.disableToolbarItems(['Delete'])
        }
        if (!permission.subcategory.rename) {
          fileObj.current.disableToolbarItems(['Rename'])
        }
      }
    }

    if (fileObj.current && (fileObj.current?.itemData[0]?.key !== 'myShared' && fileObj.current?.itemData[0]?.key !== 'myReceipts') && fileObj.current.toolbarModule && fileObj.current.toolbarModule.items) {
      fileObj.current.toolbarModule.items.forEach((item) => {
        if (item.id === 'DigitalVault_filemanager_tb_analyzefile') {
          if (!fileDetails.isFile || multiSelection) {
            item.visible = false
          } else {
            const notAnalyzedText = t('fileManager.listView.header.status.ia.no.analized')
            const statusValue = fileDetails.statusIA || fileDetails.StatusAi || fileDetails.statusAi
            item.visible = statusValue === notAnalyzedText
          }
        }
      })
      setTimeout(() => {
        const multiPost = fileObj.current && Array.isArray(fileObj.current.selectedItems) && fileObj.current.selectedItems.length > 1
        if (multiPost) {
          fileObj.current.toolbarModule.items.forEach((item) => {
            if (item.id === 'DigitalVault_filemanager_tb_analyzefile') item.visible = false
          })
        }
      }, 0)
    }

    if (fileDetails.isFile && args.action === 'select') {
      if (fileObj.current.pathNames[2]) {

        if (fileObj.current.pathNames[2] === `${process.env.REACT_APP_LOGICAL_CATEGORY_MY_SHARED}`) {
          fileObj.current.disableToolbarItems(['Copy', 'Delete'])
        }

        if (fileObj.current.pathNames[2] === `${process.env.REACT_APP_LOGICAL_CATEGORY_MY_RECEIPTS}`) {
          fileObj.current.disableToolbarItems(['Copy', 'Delete', 'Rename'])
        }
      }

      if (!permission.subcategory.copy) {
        fileObj.current.disableToolbarItems(['Copy'])
      }
      if (!permission.subcategory.download) {
        fileObj.current.disableToolbarItems(['Download'])
      }
      if (!permission.subcategory.delete) {
        fileObj.current.disableToolbarItems(['Delete'])
      }
      if (!permission.subcategory.rename) {
        fileObj.current.disableToolbarItems(['Rename'])
      }

      if (fileDetails && fileDetails.isFile && fileDetails.logical && fileDetails.key === process.env.REACT_APP_LOGICAL_CATEGORY_UNASSIGNED_SIGNATURE_KEY) {
        fileObj.current.disableToolbarItems(['Copy'])
      }

      fileObj.current.documentSelected = fileDetails
    }
  }

  const sortComparer = (a, b) => {
    try {
      const dateA = parseCustomDate({
        dateStr: a,
        preferredLanguage: language,
      })
      const dateB = parseCustomDate({
        dateStr: b,
        preferredLanguage: language,
      })

      return dateA.getTime() - dateB.getTime()
    } catch (e) {
      return 0
    }
  }

  const popupCompanies = companies.map((item) => ({
    value: item.company.id,
    name: item.company.companyName,
  }))

  let resultData = [].slice.call(fileData)

  const userLevelOptions = [
    { name: t('digitalVaultCategoriesAdministration.add.options.noAccess'), levelUser: 'roleHibouDigitalVaultNotAccess' },
    { name: t('digitalVaultCategoriesAdministration.add.options.administrator'), levelUser: 'roleHibouDigitalVaultAdministrator' },
    { name: 'N1', levelUser: 'roleHibouDigitalVaultN1' },
    { name: 'N2', levelUser: 'roleHibouDigitalVaultN2' },
    { name: 'N3', levelUser: 'roleHibouDigitalVaultN3' },
    { name: 'N4', levelUser: 'roleHibouDigitalVaultN4' },
    { name: 'N5', levelUser: 'roleHibouDigitalVaultN5' }
  ]

  function CopyDialogDetails() {
    return (
      <DialogYesNoHLPure
        btnCancel={true}
        grid={true}
        cancel={() => history.push({ pathname: '/help-desk' })}
        handleCancel={() => {
          const url = `https://wa.me/${process.env.REACT_APP_PHONE_NUMBER}?text=${encodeURIComponent(t('whatsapp_default_text'))}`
          window.open(url, '_blank', 'noopener')
        }}
        handleSubmit={() => setShowFilesCopied(false)}
        saveBtn={'OK'}
        textBtnCancel={t('fileManager.copyFile.dialog.technicalSupport')}
        titleDialog={
          actionType === 'move'
            ? t('fileManager.copyFile.dialog.heading')
            : t('fileManager.deleteFile.dialog.heading.info')
        }
      >
        <ul>
          <li>
            {`${filesSuccess}`}{' '}
            {actionType === 'move'
              ? t('fileManager.copyFile.dialog.filesCopied')
              : t('fileManager.deleteFile.dialog.filesSuccess')}
            {filesSuccessNames.length > 0 && (
              <ul>
                <li>
                  <b>{filesSuccessNames.join(', ')}</b>
                </li>
              </ul>
            )}
          </li>
          <li>
            {`${filesError}`}{' '}
            {actionType === 'move'
              ? t('fileManager.copyFile.dialog.filesNotCopied')
              : t('fileManager.deleteFile.dialog.filesError')}
            {filesErrorNames.length > 0 && (
              <ul>
                <li>
                  <b>{filesErrorNames.join(', ')}</b>
                </li>
              </ul>
            )}
          </li>
        </ul>
        <p>{t('fileManager.copyFile.dialog')}</p>
      </DialogYesNoHLPure>
    )
  }

  async function handleShowModalEntities() {
    const documentId = (fileObj && fileObj.current && fileObj.current.documentSelected) ? fileObj.current.documentSelected.id : null
    if (!documentId) {
      toggleErrorRequesting(t('digitalVaultDocumentAction.errors.no.docs.entities'))
      return
    }
    setDocumentSelectedId(documentId)
    try {
      toggleLoadingRequesting(true)
      const document = await digitalVaultDocumentService.getDocumentPages(documentId, token)
      if (document && document.data && Number(document.statusCode) === 200) {
        const docsCreditsOCR = setCreditsToDocuments(document.data.files, t)
        if (docsCreditsOCR && docsCreditsOCR.documentsToOCR && docsCreditsOCR.documentsToOCR.length > 0) {
          let docsCreditsOCR = setCreditsToDocuments(document.data.files, t)
          let docsToOCR = (docsCreditsOCR && docsCreditsOCR.documentsToOCR) ? docsCreditsOCR.documentsToOCR : []
          let totalCredits = (docsCreditsOCR && docsCreditsOCR.totalCredits) ? docsCreditsOCR.totalCredits : 0
          setDocumentsToOCR(docsToOCR)
          setTotalCredits(totalCredits)
          setShowModalEntities(true)
          toggleLoadingRequesting(false)
        } else {
          toggleLoadingRequesting(false)
          toggleErrorRequesting(t('digitalVaultDocumentAction.errors.no.docs.entities'))
        }

      } else {
        toggleLoadingRequesting(false)
        toggleErrorRequesting(t('digitalVaultDocumentAction.errors.no.docs.entities'))
      }

    } catch (error) {
      toggleLoadingRequesting(true)
      toggleErrorRequesting(t('digitalVaultDocumentAction.errors.no.docs.entities'))
    }
  }

  async function saveExtractedEntities() {
    let messageError = t('digitalVaultDocumentAction.errors.save.entities')
    setShowModalEntities(false)
    const documentId = documentSelectedId
    if (!documentId) {
      toggleErrorRequesting(messageError)
      return
    }

    try {
      const preferenceIA = await getClientPreferences()
      if (preferenceIA === process.env.REACT_APP_OPTIONS_SEND_DOCUMENT_TO_OCR_ENABLE) {
        toggleErrorRequesting('fileManager.errors.extract.entities.disable')
        return
      }

      let documentsSendToOCR = documentsToOCR.filter(doc => doc.value)
      documentsSendToOCR = documentsSendToOCR.map((doc) => {
        doc.fileId = doc.id
        return doc
      })

      if (documentsSendToOCR.length === 0) {
        toggleLoadingRequesting(false)
        toggleErrorRequesting(t('digitalVaultDocumentAction.errors.no.docs.entities'))
        return
      }

      toggleLoadingRequesting(true)
      closeModalEntities()
      const extractEntities = await digitalVaultDocumentService.sendEntitiesToExtract(documentId, documentsSendToOCR, token)
      if (extractEntities && extractEntities.data && Number(extractEntities.statusCode) === 200) {
        toggleLoadingRequesting(false)
        toggleCompletedRequesting('', true)
      } else {
        toggleLoadingRequesting(false)
        toggleErrorRequesting(messageError)
      }
    } catch (error) {
      console.error(error)
      toggleLoadingRequesting(false)
      toggleErrorRequesting(messageError)

    }

  }

  function closeModalEntities() {
    setDocumentsToOCR([])
    setTotalCredits(0)
    setShowModalEntities(false)
    fileObj.current.documentSelected = null
    setDocumentSelectedId(null)
  }

  function onClickCheckBox(e, dataItem) {
    let copyDocs = [...documentsToOCR]
    let valueCheck = e.target.checked

    // Restriction: Do not allow deselecting processId 3 if processId 4 or 6 is selected for the same file.
    if (dataItem.processId === Number(process.env.REACT_APP_EVENT_TRANSACTION_TYPE_OCR_ID) && !valueCheck) {
      const hasProcess4Or6Selected = copyDocs.some(doc =>
        doc.id === dataItem.id &&
        (doc.processId === Number(process.env.REACT_APP_EVENT_TRANSACTION_TYPE_ENTITIES_EXTRACTION_ID) || doc.processId === Number(process.env.REACT_APP_EVENT_TRANSACTION_TYPE_EMBEDDINGS_ID)) &&
        doc.value
      )

      // If there is processId 4 or 6 selected for the same file, exit without changes
      if (hasProcess4Or6Selected) {
        return
      }
    }

    // Toggle the value property of the target document
    copyDocs = copyDocs.map((doc) => {
      if (doc.id === dataItem.id && doc.processId === dataItem.processId) {
        doc.value = valueCheck
      }
      return doc
    })

    // Auto-selection logic: If the current document is processId 4 or 6, manage processId 3 of the same file
    if (dataItem.processId === Number(process.env.REACT_APP_EVENT_TRANSACTION_TYPE_ENTITIES_EXTRACTION_ID) || dataItem.processId === Number(process.env.REACT_APP_EVENT_TRANSACTION_TYPE_EMBEDDINGS_ID)) {
      // Find processId 3 of the same file (same id, different processId)
      const process3SameFile = copyDocs.find(doc => doc.id === dataItem.id && doc.processId === Number(process.env.REACT_APP_EVENT_TRANSACTION_TYPE_OCR_ID))

      if (process3SameFile) {
        if (valueCheck) {
          // If processId 4 or 6 is being selected, auto-select processId 3 of the same file
          process3SameFile.value = true
        } else {
          // If processId 4 or 6 is being deselected, check if there are other 4 or 6 selected for the same file
          const hasOtherProcess4Or6Selected = copyDocs.some(doc =>
            doc.id === dataItem.id &&
            (doc.processId === Number(process.env.REACT_APP_EVENT_TRANSACTION_TYPE_ENTITIES_EXTRACTION_ID) || doc.processId === Number(process.env.REACT_APP_EVENT_TRANSACTION_TYPE_EMBEDDINGS_ID)) &&
            doc.processId !== dataItem.processId &&
            doc.value
          )

          // If no other processId 4 or 6 is selected for this file, deselect processId 3
          if (!hasOtherProcess4Or6Selected) {
            process3SameFile.value = false
          }
        }
      }
    }

    // Recalculate total credits
    let totalCredits = 0
    copyDocs.forEach(doc => {
      if (doc.value) {
        totalCredits += doc.credits || 0
      }
    })
    setDocumentsToOCR(copyDocs)
    setTotalCredits(totalCredits)
  }

  useEffect(() => {
    if (
      selectedCompany &&
      selectedCompany.company &&
      selectedCompany.company.id
    ) {
      setCompanyId(selectedCompany.company.id)
    }
  }, [selectedCompany])

  useEffect(() => {
    if (companyId && timezone) {
      getCategories()
    }
  }, [companyId, timezone])

  useEffect(() => {
    return () => {
      setFileManagerPath('/')
    }
  }, [companyId])

  useEffect(() => {
    if (pendingSubcategoryId) {
      getDocumentsBySubcategory(pendingSubcategoryId, true, fileDetails)
      setPendingSubcategoryId(null)
    }
  }, [fileData, pendingSubcategoryId])

  useEffect(() => {
    if (state && state.subcategoryPath && !firstLoad) {
      getDocumentsBySubcategory(state.subcategoryPath, true)
    }

    if (subcategoryId && !firstLoad) {
      getDocumentsBySubcategory(subcategoryId, true)
    }
  }, [subcategoryId, state, firstLoad])

  useEffect(() => {
    async function loadL10n() {
      const response = await fetch(
        '/assets/syncfusion/translations/file-manager.json'
      )
      const locales = await response.json()
      if (locales[language]) {
        L10n.load({
          [language]: locales[language],
        })
      }
    }
    loadL10n()
  }, [language])

  const getClientPreferences = async () => {
    try {
      const clientPreferences = await clientPreferencesService.getClientPreferences(token, timezone)
      let preferenceIAFound = clientPreferences.data.find(preference => preference.preferenceName === process.env.REACT_APP_PREFERENCE_DOCUMENT_TO_OCR)
      const preferenceIA = (preferenceIAFound && preferenceIAFound.stringValue) ? preferenceIAFound.stringValue : process.env.REACT_APP_OPTIONS_SEND_DOCUMENT_TO_OCR_ON_DEMAND
      return preferenceIA
    } catch (error) {
      return process.env.REACT_APP_OPTIONS_SEND_DOCUMENT_TO_OCR_ON_DEMAND
    }

  }

  if (isLoading && firstLoad) return <SimpleLoading />

  return (
    <>
      <DialogSystemGLPure
        showLoadingRequesting={showLoadingRequesting}
        toggleLoadingRequesting={toggleLoadingRequesting}
        showCompletedRequesting={showCompletedRequesting}
        toggleCompletedRequesting={toggleCompletedRequesting}
        showErrorRequesting={showErrorRequesting}
        toggleErrorRequesting={toggleErrorRequesting}
        isWarnign={showDeleteWarning}
        handleWarningClose={() => setShowDeleteWarning(false)}
        titleDialog={showDeleteWarning ? deleteWarningTitle : successDialogTitle}
        messageWarning={deleteWarningMessage}
        showBtnCancelWarning={false}
        showBtnSubmitWarning={true}
        labelWarningBtnSubmit={t('button.back')}
        handleWarningSubmit={() => setShowDeleteWarning(false)}
        message={messageDialog}
        messageComplete={messageDialog}
        isJustified={true}
      />
      {showFilesCopied && <CopyDialogDetails />}
      <SyncfusionFileManager
        addNewFile={addNewFile}
        categoriesData={categoriesData}
        checkDepth={checkDepth}
        checkPermission={checkPermission}
        companies={popupCompanies}
        companyId={companyId}
        copyFiles={copyFiles}
        createNewCategory={createNewCategory}
        deleteFiles={deleteFiles}
        documentsData={documentsData}
        downloadFile={downloadFile}
        fileManagerPath={fileManagerPath}
        fileObj={fileObj}
        fileSystemData={resultData}
        isLoading={isLoading}
        language={language}
        moveFiles={moveFiles}
        onCreated={onCreated}
        onFileOpen={onFileOpen}
        onFileSelect={onFileSelect}
        onMenuOpen={onMenuOpen}
        permission={permission}
        renameFile={renameFile}
        refreshFileManager={refreshFileManager}
        setCompanyId={setCompanyId}
        setIsLoading={setIsLoading}
        setFileDetails={setFileDetails}
        setFirstLoad={setFirstLoad}
        showAddFileBtn={showAddFileBtn}
        sortComparer={sortComparer}
        strBtn={strBtn}
        t={t}
        userLevelOptions={userLevelOptions}
        toggleErrorRequesting={toggleErrorRequesting}
        showModalEntities={showModalEntities}
        handleShowModalEntities={handleShowModalEntities}
        closeModalEntities={closeModalEntities}
        saveExtractedEntities={saveExtractedEntities}
        documentsToOCR={documentsToOCR}
        totalCredits={totalCredits}
        onClickCheckBox={onClickCheckBox}
      />
    </>
  )
}

export default SyncfusionFileManagerAction